import React, { memo } from 'react'
import { useRecoilValue } from 'recoil'
import { toggleMenu as toggleMenuState } from '../../store/uiStore'
import { AnimatePresence } from 'framer-motion'
import useWindowResize from '../../hooks/useWindowResize'
import { theme, MElement } from '../../styles/globalStyles'
import { MenuLayer } from '../../styles/headerStyles'

import Polygon from './Polygon'

const Background = () => {
  const toggleMenu = useRecoilValue(toggleMenuState)
  const windowSize = useWindowResize()

  const baseCoords = () => {
    if (windowSize.width >= 1900) {
      return {
        x: '50%',
        y: 0,
        d: 2.25,
        r: 150,
      }
    } else if (windowSize.width >= 1680) {
      return {
        x: '50%',
        y: 0,
        d: 2,
        r: 150,
      }
    } else if (windowSize.width >= 1536) {
      return {
        x: '50%',
        y: 0,
        d: 2.35,
        r: 150,
      }
    } else if (windowSize.width >= 1440) {
      return {
        x: '50%',
        y: 0,
        d: 1.95,
        r: 150,
      }
    } else if (windowSize.width >= 1360) {
      return {
        x: '50%',
        y: 0,
        d: 2.385,
        r: 150,
      }
    } else if (windowSize.width >= 1280) {
      return {
        x: '50%',
        y: 0,
        d: 2.5,
        r: 150,
      }
    } else if (windowSize.width > 1024 && windowSize.width < 1280) {
      return {
        x: '50%',
        y: 0,
        d: 2.5,
        r: 150,
      }
    } else if (windowSize.width <= 1024) {
      if (windowSize.width > 420) {
        return {
          x: '30%',
          y: 0,
          d: 2.25,
          r: 150,
        }
      } else {
        return {
          x: '30%',
          y: windowSize.height - windowSize.height * 0.8,
          d: 2.5,
          r: 150,
        }
      }
    }
  }

  const greenCoords = () => {
    if (windowSize.width >= 1900) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.25,
        r: -235,
      }
    } else if (windowSize.width >= 1680) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.25,
        r: -235,
      }
    } else if (windowSize.width >= 1536) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.35,
        r: -235,
      }
    } else if (windowSize.width >= 1440) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.3,
        r: -235,
      }
    } else if (windowSize.width >= 1360) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.3,
        r: -235,
      }
    } else if (windowSize.width >= 1280) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.3,
        r: -235,
      }
    } else if (windowSize.width > 1024 && windowSize.width < 1280) {
      return {
        x: '-75%',
        y: '-40%',
        d: 1.3,
        r: -235,
      }
    } else if (windowSize.width <= 1024) {
      if (windowSize.width > 420) {
        return {
          x: '-85%',
          y: '-05%',
          d: 1.3,
          r: -205,
        }
      } else {
        return {
          x: '-100%',
          y: windowSize.height - windowSize.height * 0.8,
          d: 2,
          r: -217,
        }
      }
    }
  }

  const orangeCoords = () => {
    if (windowSize.width >= 1900) {
      return {
        x: '-40%',
        y: '45%',
        d: 0.85,
        r: 222,
      }
    } else if (windowSize.width >= 1680) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.75,
        r: 222,
      }
    } else if (windowSize.width >= 1536) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.85,
        r: 222,
      }
    } else if (windowSize.width >= 1440) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.75,
        r: 222,
      }
    } else if (windowSize.width >= 1360) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.85,
        r: 222,
      }
    } else if (windowSize.width >= 1280) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.85,
        r: 222,
      }
    } else if (windowSize.width > 1024 && windowSize.width < 1280) {
      return {
        x: '-45%',
        y: '40%',
        d: 0.85,
        r: 222,
      }
    } else if (windowSize.width <= 1024) {
      if (windowSize.width > 420) {
        return {
          x: '-45%',
          y: '75%',
          d: 0.85,
          r: 222,
        }
      } else {
        return {
          x: '-60%',
          y: windowSize.height - windowSize.height * 0.2,
          d: 1.7,
          r: 222,
        }
      }
    }
  }

  const baseVariants = {
    hidden: {
      opacity: 0,
      rotate: 0,
      x: '50%',
      y: '-150%',
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
    visible: {
      x: baseCoords().x,
      y: baseCoords().y,
      rotate: baseCoords().r,
      opacity: 1,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
  }
  const greenVariants = {
    hidden: {
      opacity: 0,
      x: '-100%',
      y: '-100%',
      rotate: 0,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
    visible: {
      opacity: 1,
      x: greenCoords().x,
      y: greenCoords().y,
      rotate: greenCoords().r,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
  }
  const orangeVariants = {
    hidden: {
      opacity: 0,
      x: '-100%',
      y: '100%',
      rotate: 0,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
    visible: {
      opacity: 1,
      x: orangeCoords().x,
      y: orangeCoords().y,
      rotate: orangeCoords().r,
      transition: {
        duration: 1,
        ease: 'easeOut',
      },
    },
  }

  return (
    <AnimatePresence exitBeforeEnter>
      {toggleMenu && (
        <MenuLayer>
          <MElement
            style={{
              scale: baseCoords().d,
            }}
            initial='hidden'
            animate='visible'
            exit='hidden'
            variants={baseVariants}>
            <Polygon shape='octagon' fill={theme.blue} />
          </MElement>
          <MElement
            style={{
              scale: greenCoords().d,
            }}
            initial='hidden'
            animate='visible'
            exit='hidden'
            variants={greenVariants}>
            <Polygon shape='octagon' fill={theme.green} />
          </MElement>
          <MElement
            style={{
              scale: orangeCoords().d,
            }}
            initial='hidden'
            animate='visible'
            exit='hidden'
            variants={orangeVariants}>
            <Polygon shape='heptagon' fill={theme.orange} />
          </MElement>
        </MenuLayer>
      )}
    </AnimatePresence>
  )
}

export default memo(Background)
